/**
 * Progressively load interactive SVG elevation profiles near the viewport.
 * The raster poster remains a complete, indexable no-JavaScript fallback.
 */
export function initElevationProfiles() {
  const containers = Array.from(document.querySelectorAll('[data-elevation-profile]'));
  if (!containers.length) return;

  const enhance = async container => {
    if (container.dataset.profileEnhancing === 'true' || container.classList.contains('is-interactive')) return;

    const source = container.dataset.profileSvgUrl;
    const visual = container.querySelector('[data-elevation-profile-visual]');
    if (!source || !visual) return;

    container.dataset.profileEnhancing = 'true';

    try {
      const response = await fetch(source, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Elevation profile request failed (${response.status})`);

      const documentNode = new DOMParser().parseFromString(await response.text(), 'image/svg+xml');
      const svg = documentNode.documentElement;
      if (svg.localName !== 'svg' || documentNode.querySelector('parsererror')) {
        throw new Error('Invalid elevation profile SVG');
      }

      const importedSvg = document.importNode(svg, true);
      visual.replaceChildren(importedSvg);
      container.classList.add('is-interactive');
      container.querySelector('.elevation-fullscreen-btn')?.removeAttribute('hidden');
      bindInteractiveProfile(container, importedSvg);
    } catch (error) {
      // The poster is intentionally kept in place if enhancement fails.
      console.warn('Unable to enhance elevation profile:', error);
    } finally {
      delete container.dataset.profileEnhancing;
    }
  };

  if (!('IntersectionObserver' in window)) {
    containers.forEach(enhance);
    return;
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      enhance(entry.target);
    });
  }, { rootMargin: '300px 0px' });

  containers.forEach(container => observer.observe(container));
}

function bindInteractiveProfile(container, svg) {
  const tooltip = container.querySelector('.tooltip');
  const crosshair = svg.querySelector('.crosshair');
  if (!tooltip || !crosshair) return;

  const profile = JSON.parse(svg.dataset.profile);
  const distMax = +svg.dataset.distMax;
  const eleMin = +svg.dataset.eleMin;
  const eleMax = +svg.dataset.eleMax;
  const ml = +svg.dataset.ml;
  const mr = +svg.dataset.mr;
  const mt = +svg.dataset.mt;
  const mb = +svg.dataset.mb;
  const vbW = 800;
  const chartW = vbW - ml - mr;
  const chartH = 300 - mt - mb;
  let modal = null;
  let modalSvg = null;
  let modalTooltip = null;

  function interp(km) {
    for (let i = 1; i < profile.length; i++) {
      if (profile[i][0] >= km) {
        const t = (km - profile[i - 1][0]) / (profile[i][0] - profile[i - 1][0]);
        return {
          ele: profile[i - 1][1] + t * (profile[i][1] - profile[i - 1][1]),
          dpos: Math.round(profile[i - 1][2] + t * (profile[i][2] - profile[i - 1][2])),
          dneg: Math.round(profile[i - 1][3] + t * (profile[i][3] - profile[i - 1][3])),
        };
      }
    }
    const last = profile[profile.length - 1];
    return { ele: last[1], dpos: last[2], dneg: last[3] };
  }

  function toVbX(km) { return ml + (km / distMax) * chartW; }
  function toVbY(ele) { return mt + (1 - (ele - eleMin) / (eleMax - eleMin)) * chartH; }

  function showCrosshair(clientX, targetSvg) {
    const rect = targetSvg.getBoundingClientRect();
    const vbX = ((clientX - rect.left) / rect.width) * vbW;
    const km = Math.max(0, Math.min(distMax, ((vbX - ml) / chartW) * distMax));
    const { ele, dpos, dneg } = interp(km);
    const cx = toVbX(km);
    const cy = toVbY(ele);

    for (const currentSvg of [svg, modalSvg].filter(Boolean)) {
      const currentCrosshair = currentSvg.querySelector('.crosshair');
      if (!currentCrosshair) continue;
      const line = currentCrosshair.querySelector('.ch-line');
      const dot = currentCrosshair.querySelector('.ch-dot');
      line?.setAttribute('x1', cx);
      line?.setAttribute('x2', cx);
      dot?.setAttribute('cx', cx);
      dot?.setAttribute('cy', cy);
      currentCrosshair.style.display = '';
    }

    const currentTooltip = modal?.classList.contains('is-open') ? modalTooltip : tooltip;
    if (!currentTooltip) return;
    currentTooltip.querySelector('.tt-dist').textContent = `↔ ${km.toFixed(1)} km`;
    currentTooltip.querySelector('.tt-alt').textContent = `▲ ${Math.round(ele)} m`;
    currentTooltip.querySelector('.tt-dpos').textContent = `↗ ${dpos} m`;
    currentTooltip.querySelector('.tt-dneg').textContent = `↘ ${dneg} m`;
    currentTooltip.style.display = '';
  }

  function hideCrosshair() {
    for (const currentSvg of [svg, modalSvg].filter(Boolean)) {
      const currentCrosshair = currentSvg.querySelector('.crosshair');
      if (currentCrosshair) currentCrosshair.style.display = 'none';
    }
    tooltip.style.display = 'none';
    if (modalTooltip) modalTooltip.style.display = 'none';
  }

  function bindSvgEvents(targetSvg) {
    targetSvg.addEventListener('mousemove', event => showCrosshair(event.clientX, targetSvg));
    targetSvg.addEventListener('mouseleave', hideCrosshair);
    targetSvg.addEventListener('touchmove', event => {
      event.preventDefault();
      showCrosshair(event.touches[0].clientX, targetSvg);
    }, { passive: false });
    targetSvg.addEventListener('touchstart', event => {
      event.preventDefault();
      showCrosshair(event.touches[0].clientX, targetSvg);
    }, { passive: false });
    targetSvg.addEventListener('touchend', hideCrosshair);
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    hideCrosshair();
  }

  function onEsc(event) {
    if (event.key === 'Escape') closeModal();
  }

  function openModal() {
    document.querySelectorAll('.elevation-modal.is-open').forEach(element => element.classList.remove('is-open'));

    if (modal) {
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      return;
    }

    modal = document.createElement('div');
    modal.className = 'elevation-modal is-open';
    modal.innerHTML = `
      <div class="elevation-modal-backdrop"></div>
      <div class="elevation-modal-content">
        <button type="button" class="elevation-modal-close">&times;</button>
        <div class="elevation-modal-svg-wrap">${svg.outerHTML}</div>
        <div class="tooltip" style="display:none">
          <span class="tt-dist"></span>
          <span class="tt-alt"></span>
          <span class="tt-dpos"></span>
          <span class="tt-dneg"></span>
        </div>
      </div>`;
    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    modalSvg = modal.querySelector('svg');
    modalTooltip = modal.querySelector('.tooltip');
    modal.querySelector('.elevation-modal-close').setAttribute('aria-label', container.dataset.closeLabel || 'Close');
    modalSvg.querySelectorAll('.poi-label').forEach(element => { element.style.display = 'block'; });
    bindSvgEvents(modalSvg);

    modal.querySelector('.elevation-modal-close').addEventListener('click', closeModal);
    modal.querySelector('.elevation-modal-backdrop').addEventListener('click', closeModal);
    document.addEventListener('keydown', onEsc);
  }

  bindSvgEvents(svg);

  container.querySelector('.elevation-fullscreen-btn')?.addEventListener('click', event => {
    event.stopPropagation();
    openModal();
  });

  let lastTap = 0;
  svg.addEventListener('touchend', () => {
    const now = Date.now();
    if (now - lastTap < 350) {
      openModal();
      lastTap = 0;
    } else {
      lastTap = now;
    }
  });
}
